export type IdentificationRule = '3-property' | '200-percent' | '95-percent'

export interface PropertyForRules {
  estimatedFMV: number
  allocationAmount?: number
  type?: 'dst' | 'like-kind' | string
  underlyingAssets?: ReadonlyArray<{ address: string }>
}

export interface RuleValidationResult {
  valid: boolean
  blockReason?: string
}

export interface AddPropertyValidation extends RuleValidationResult {
  canAdd: boolean
  disabledReason?: string
}

export interface SubmitValidation extends RuleValidationResult {
  warnings: string[]
}

const NINETY_FIVE_PERCENT = 0.95
const TWO_HUNDRED_PERCENT = 2.0
const FMV_TOLERANCE = 1

function formatUSD(value: number): string {
  return Math.round(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function countIdentifiedInterests(properties: PropertyForRules[]): number {
  return properties.length
}

function sumFMV(properties: PropertyForRules[]): number {
  return properties.reduce((sum, p) => sum + (p.estimatedFMV || 0), 0)
}

function sumAllocation(properties: PropertyForRules[]): number {
  return properties.reduce((sum, p) => sum + (p.allocationAmount || 0), 0)
}

export function evaluateRule(
  rule: IdentificationRule,
  properties: PropertyForRules[],
  relinquishedFMV: number | null,
): RuleValidationResult {
  if (rule === '3-property') {
    if (countIdentifiedInterests(properties) > 3) {
      return { valid: false, blockReason: 'Cannot exceed 3 properties under the 3-Property Rule.' }
    }
    return { valid: true }
  }

  if (rule === '200-percent') {
    if (relinquishedFMV == null || relinquishedFMV <= 0) {
      return {
        valid: false,
        blockReason: 'Relinquished FMV required to evaluate the 200% rule. Upload and confirm your settlement statement.',
      }
    }
    const aggregateFMV = sumFMV(properties)
    const limit = relinquishedFMV * TWO_HUNDRED_PERCENT
    if (aggregateFMV > limit + FMV_TOLERANCE) {
      return {
        valid: false,
        blockReason: `Aggregate identified FMV ${formatUSD(aggregateFMV)} exceeds 200% of relinquished FMV (${formatUSD(limit)}).`,
      }
    }
    return { valid: true }
  }

  return { valid: true }
}

export function canAddProperty(
  rule: IdentificationRule,
  existing: PropertyForRules[],
  candidateFMV: number,
  relinquishedFMV: number | null,
): AddPropertyValidation {
  if (rule === '3-property') {
    if (countIdentifiedInterests(existing) >= 3) {
      return {
        valid: false,
        canAdd: false,
        disabledReason: "You've reached the 3-property limit. Switch to the 200% rule to add more.",
      }
    }
    return { valid: true, canAdd: true }
  }

  if (rule === '200-percent') {
    if (relinquishedFMV == null || relinquishedFMV <= 0) {
      return { valid: true, canAdd: true }
    }
    const currentTotal = sumFMV(existing)
    const projectedTotal = currentTotal + candidateFMV
    const limit = relinquishedFMV * TWO_HUNDRED_PERCENT
    if (projectedTotal > limit + FMV_TOLERANCE) {
      return {
        valid: false,
        canAdd: false,
        disabledReason: `Adding this property (${formatUSD(candidateFMV)} FMV) would push aggregate identified FMV to ${formatUSD(projectedTotal)}, exceeding the 200% limit (${formatUSD(limit)}). Switch to the 95% rule to add more.`,
      }
    }
    return { valid: true, canAdd: true }
  }

  return { valid: true, canAdd: true }
}

export function validateIdentificationSubmit(
  rule: IdentificationRule,
  properties: PropertyForRules[],
  relinquishedFMV: number | null,
): SubmitValidation {
  if (rule === '95-percent') {
    const totalFMV = sumFMV(properties)
    const totalAllocation = sumAllocation(properties)
    const warnings: string[] = []
    if (totalFMV > 0 && totalAllocation < totalFMV * NINETY_FIVE_PERCENT - FMV_TOLERANCE) {
      warnings.push(
        `Under the 95% rule, planned allocation ${formatUSD(totalAllocation)} is less than 95% of identified aggregate FMV ${formatUSD(totalFMV)}. You must actually close on at least 95% to preserve §1031 status.`,
      )
    }
    return { valid: true, warnings }
  }

  const result = evaluateRule(rule, properties, relinquishedFMV)
  return { ...result, warnings: [] }
}

export function validatePurchaseClose(
  rule: IdentificationRule,
  identifiedProperties: PropertyForRules[],
  closedFMVTotal: number,
): RuleValidationResult {
  if (rule !== '95-percent') return { valid: true }

  const totalIdentifiedFMV = sumFMV(identifiedProperties)
  const requiredClosing = totalIdentifiedFMV * NINETY_FIVE_PERCENT
  if (closedFMVTotal < requiredClosing - FMV_TOLERANCE) {
    return {
      valid: false,
      blockReason: `95% rule requires closing on at least ${formatUSD(requiredClosing)} (95% of identified ${formatUSD(totalIdentifiedFMV)}); only ${formatUSD(closedFMVTotal)} closed.`,
    }
  }
  return { valid: true }
}

export type AllocationReconciliationStatus = 'balanced' | 'over' | 'under' | 'empty'

export interface AllocationReconciliationResult {
  status: AllocationReconciliationStatus
  idHubTotal: number
  finalizedTotal: number
  delta: number
}

export function reconcileFinalizedAllocations(
  identifiedProperties: ReadonlyArray<{ allocationAmount?: number; estimatedFMV: number }>,
  finalizedAmounts: ReadonlyArray<number>,
): AllocationReconciliationResult {
  const idHubTotal = identifiedProperties.reduce(
    (sum, p) => sum + (p.allocationAmount ?? p.estimatedFMV ?? 0),
    0,
  )
  const finalizedTotal = finalizedAmounts.reduce((sum, a) => sum + (a ?? 0), 0)
  const delta = finalizedTotal - idHubTotal

  if (idHubTotal === 0 && finalizedTotal === 0) {
    return { status: 'empty', idHubTotal, finalizedTotal, delta: 0 }
  }

  if (Math.abs(delta) <= FMV_TOLERANCE) {
    return { status: 'balanced', idHubTotal, finalizedTotal, delta }
  }

  return {
    status: delta > 0 ? 'over' : 'under',
    idHubTotal,
    finalizedTotal,
    delta,
  }
}

export interface FinalizedAcquisitionComplianceResult extends RuleValidationResult {
  reconciliation: AllocationReconciliationResult
}

export function validateFinalizedAcquisitions(
  rule: IdentificationRule,
  identifiedProperties: PropertyForRules[],
  finalizedAmounts: ReadonlyArray<number>,
): FinalizedAcquisitionComplianceResult {
  const reconciliation = reconcileFinalizedAllocations(identifiedProperties, finalizedAmounts)
  const closedFMVTotal = reconciliation.finalizedTotal

  const closeResult = validatePurchaseClose(rule, identifiedProperties, closedFMVTotal)

  return {
    valid: reconciliation.status === 'balanced' && closeResult.valid,
    blockReason: closeResult.blockReason,
    reconciliation,
  }
}
